/**
 * Utility functions for working with daily goals
 * Handles day-specific goals, rest days, and timezone-aware date calculations
 */

/**
 * Get the day of week from a date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * Uses local timezone
 */
export function getDayOfWeek(date: string | Date): number {
  const dateObj =
    typeof date === "string" ? new Date(date + "T00:00:00") : date;
  return dateObj.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
}

/**
 * Convert a date to user's local date string (YYYY-MM-DD)
 * This ensures logs are saved to the correct day based on user's timezone
 */
export function getLocalDateString(date?: Date): string {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get goal for a specific date from goals object.
 * Each day has its own column (monday_goal_minutes, etc.).
 * null = rest day (no goal). A number = goal in minutes.
 */
export function getGoalForDate(
  goals: {
    monday_goal_minutes?: number | null;
    tuesday_goal_minutes?: number | null;
    wednesday_goal_minutes?: number | null;
    thursday_goal_minutes?: number | null;
    friday_goal_minutes?: number | null;
    saturday_goal_minutes?: number | null;
    sunday_goal_minutes?: number | null;
  },
  date: string | Date,
): number | null {
  const dayOfWeek = getDayOfWeek(date);

  // Map day of week (0=Sunday, 1=Monday, ..., 6=Saturday) to goal columns
  let goal: number | null | undefined;
  switch (dayOfWeek) {
    case 0:
      goal = goals.sunday_goal_minutes;
      break;
    case 1:
      goal = goals.monday_goal_minutes;
      break;
    case 2:
      goal = goals.tuesday_goal_minutes;
      break;
    case 3:
      goal = goals.wednesday_goal_minutes;
      break;
    case 4:
      goal = goals.thursday_goal_minutes;
      break;
    case 5:
      goal = goals.friday_goal_minutes;
      break;
    case 6:
      goal = goals.saturday_goal_minutes;
      break;
  }

  // null = rest day, undefined = column not in object (treat as null/rest day)
  if (goal == null) return null;
  return goal;
}

/**
 * Compute the total weekly goal by summing per-day goals for the current week.
 * Rest days (null) contribute 0 minutes.
 */
export function getWeeklyGoalTotal(
  goals: {
    monday_goal_minutes?: number | null;
    tuesday_goal_minutes?: number | null;
    wednesday_goal_minutes?: number | null;
    thursday_goal_minutes?: number | null;
    friday_goal_minutes?: number | null;
    saturday_goal_minutes?: number | null;
    sunday_goal_minutes?: number | null;
  },
): number {
  let total = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + i); // Sunday through Saturday
    const g = getGoalForDate(goals, d);
    if (g !== null) total += g;
  }
  return total;
}
